/** Rebuild scopes, and "rebuild, then publish" — one meaning for the game
 *  page and the Builds tab's club row (Ben, 2026-10-29).
 *
 *  latest — each pack's latest version, rebuilt whatever is stored
 *  all    — every version ever registered, rebuilt whatever is stored
 *  failed — each pack's latest version, only where it is not already a
 *           current succeeded build (missing, failed or stale) on the game
 *           page; on the Builds tab's club row, only where its last build
 *           FAILED (Ben, 2026-10-30) — a never-built or stale report there
 *           is "Latest versions only"
 */
import { listRuns, publishClubGreen, type ClubPublishResult } from './promotions'

export type RebuildScope = 'latest' | 'all' | 'failed'

export const REBUILD_SCOPES: { value: RebuildScope; label: string }[] = [
  { value: 'latest', label: 'Latest versions only' },
  { value: 'all', label: 'All versions' },
  { value: 'failed', label: 'Failed only' },
]

type PackRef = { name: string; version: string }

/** Failed reads of the build queue in a row before the wait gives up. */
const MAX_POLL_MISSES = 5

/** Wait until none of the club's runs of these packs (on `gameCode`, or
 *  on every game when null) is queued or running, then publish the green
 *  ones at the versions given — the Builds tab's Publish green, which
 *  skips a page built before the data changed and counts what it left.
 *
 *  It waits in this browser tab: closing the tab before the builds finish
 *  publishes nothing (the builds still run). Rejects after `timeoutMs`
 *  with nothing published. `onProgress` hears how many builds are left
 *  on each read of the queue. */
export async function publishWhenBuilt({
  orgId, gameCode, packs, reason, pollMs = 10_000, timeoutMs = 2 * 60 * 60_000,
  onProgress,
}: {
  orgId: number; gameCode: string | null; packs: PackRef[]; reason: string
  pollMs?: number; timeoutMs?: number; onProgress?: (left: number) => void
}): Promise<ClubPublishResult> {
  const names = new Set(packs.map((p) => p.name))
  const deadline = Date.now() + timeoutMs
  let misses = 0
  for (;;) {
    let runs
    try {
      ({ runs } = await listRuns())
      misses = 0
    } catch (e) {
      // A blip must not throw away a wait whose builds are still running;
      // only a run of failures in a row gives up.
      if (++misses >= MAX_POLL_MISSES) throw e
      await new Promise((resolve) => setTimeout(resolve, pollMs))
      continue
    }
    const left = runs.filter((r) => r.org_id === orgId
      && (gameCode === null || r.game_code === gameCode)
      && names.has(r.pack_id)
      && (r.status === 'queued' || r.status === 'running')).length
    onProgress?.(left)
    if (!left) break
    if (Date.now() > deadline) {
      throw new Error('the builds were still running after '
        + `${Math.round(timeoutMs / 60_000)} min — nothing was published`)
    }
    await new Promise((resolve) => setTimeout(resolve, pollMs))
  }
  return publishClubGreen(orgId, packs, gameCode, reason)
}

/** The alert line for a publish result. */
export function publishSummary(r: ClubPublishResult): string {
  const notGreen = Object.values(r.not_green).reduce((n, v) => n + v, 0)
  return `published ${r.published}`
    + (r.already ? `; ${r.already} already published` : '')
    + (r.stale ? `; ${r.stale} built before the data changed — rebuild those first` : '')
    + (notGreen ? `; ${notGreen} not green (not built, building or failed)` : '')
}
